// src/pages/AuthCallback.tsx
// Production-safe — all console logs, debug UI, and log state removed
// Core auth logic (session check, code exchange, profile lookup/insert, routing) untouched
import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../config/supabaseClient';
import toast from 'react-hot-toast';

export const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // STEP 1: Get session
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          toast.error('Authentication failed. Please try again.');
          setTimeout(() => navigate('/login'), 2000);
          return;
        }

        if (!sessionData.session) {
          // No session — try exchanging code from URL
          const urlParams  = new URLSearchParams(window.location.search);
          const hashParams = new URLSearchParams(window.location.hash.replace('#', ''));
          const code       = urlParams.get('code') || hashParams.get('code');

          if (!code) {
            toast.error('Authentication failed. Please try again.');
            setTimeout(() => navigate('/login'), 2000);
            return;
          }

          const { error: codeExchangeError } =
            await supabase.auth.exchangeCodeForSession(code);

          if (codeExchangeError) {
            toast.error('Authentication failed. Please try again.');
            setTimeout(() => navigate('/login'), 2000);
            return;
          }
        }

        // STEP 2: Get final session
        const { data: finalSessionData } = await supabase.auth.getSession();
        const session = finalSessionData.session;

        if (!session) {
          toast.error('Authentication failed. Please try again.');
          setTimeout(() => navigate('/login'), 2000);
          return;
        }

        const userId    = session.user.id;
        const userEmail = session.user.email;
        const avatarUrl = session.user.user_metadata?.avatar_url
                       || session.user.user_metadata?.picture;

        // STEP 3: Check for existing profile
        const { data: profile, error: profileError } = await supabase
          .from('users')
          .select('*')
          .eq('id', userId)
          .single();

        if (profileError) {
          if (profileError.code === 'PGRST116') {
            // New user — create profile with temp username
            const tempUsername = `temp_${userId.substring(0, 8)}`;

            const newProfile = {
              id:               userId,
              email:            userEmail,
              username:         tempUsername,
              avatar_url:       avatarUrl || `https://api.dicebear.com/7.x/avataaars/svg?seed=${tempUsername}`,
              level:            1,
              ispremium:        false,
              bio:              '',
              location:         '',
              website:          '',
              favorite_anime:   [],
              followers_count:  0,
              following_count:  0,
              posts_count:      0,
              profile_completed: false,
            };

            const { error: insertError } = await supabase
              .from('users')
              .insert(newProfile);

            if (insertError) {
              toast.error('Failed to create profile. Please try again.');
              setTimeout(() => navigate('/login'), 2000);
              return;
            }

            toast.success('Account created! Complete your profile 🎉');
            setTimeout(() => navigate('/profile-setup', { replace: true }), 1500);
            return;
          }

          // Any other profile fetch error
          toast.error('Something went wrong. Please try again.');
          setTimeout(() => navigate('/login'), 2000);
          return;
        }

        // STEP 4: Route based on profile completion
        if (!profile.profile_completed) {
          toast.success('Welcome! Complete your profile 🎨');
          setTimeout(() => navigate('/profile-setup', { replace: true }), 1500);
        } else {
          toast.success(`Welcome back, ${profile.username}! ⚡`);
          setTimeout(() => navigate('/home', { replace: true }), 1500);
        }

      } catch {
        toast.error('Something went wrong. Please try again.');
        setTimeout(() => navigate('/login'), 2000);
      }
    };

    handleCallback();
  }, [navigate, location]);

  // ── CLEAN LOADING UI ─────────────────────────────────────────
  return (
    <div style={{
      minHeight: '100vh',
      background: '#0d1a1a',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'M PLUS Rounded 1c', sans-serif",
      gap: 20,
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=M+PLUS+Rounded+1c:wght@700;900&display=swap');
        @keyframes spin  { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
      `}</style>

      {/* Ronyx shogi piece logo */}
      <svg width="52" height="58" viewBox="0 0 100 110" style={{ marginBottom: 4 }}>
        <defs>
          <linearGradient id="cb-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#a8e6e6" />
            <stop offset="100%" stopColor="#7ecece" />
          </linearGradient>
        </defs>
        <path
          d="M50 8 L82 22 L88 88 Q88 96 80 96 L20 96 Q12 96 12 88 L18 22 Z"
          fill="url(#cb-grad)"
          stroke="#0d1a1a"
          strokeWidth="3"
          strokeLinejoin="round"
        />
        {/* Slash detail */}
        <line x1="28" y1="72" x2="68" y2="28" stroke="rgba(13,26,26,0.25)" strokeWidth="3" strokeLinecap="round" />
        <line x1="32" y1="80" x2="72" y2="36" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" strokeLinecap="round" />
      </svg>

      {/* Spinner */}
      <div style={{
        width: 44, height: 44, borderRadius: '50%',
        border: '3px solid rgba(90,184,184,0.15)',
        borderTop: '3px solid #7ecece',
        animation: 'spin 0.8s linear infinite',
      }} />

      {/* Text */}
      <div style={{ textAlign: 'center' }}>
        <p style={{
          fontFamily: "'Bebas Neue', sans-serif",
          fontSize: 18, letterSpacing: '4px',
          color: '#7ecece', margin: 0, lineHeight: 1,
        }}>
          SIGNING YOU IN
        </p>
        <p style={{
          fontSize: 11, fontWeight: 700,
          color: 'rgba(126,206,206,0.4)',
          letterSpacing: '2px', marginTop: 6,
          animation: 'pulse 1.8s ease-in-out infinite',
        }}>
          PLEASE WAIT...
        </p>
      </div>
    </div>
  );
};

export default AuthCallback;